import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Checkbox,
  Avatar,
  Button,
} from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplyIcon from "@mui/icons-material/Reply";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import AttachmentPreview from "../preview/AttachmentPreview";
import { useUniboxColors } from "../../useUniboxColors";
import ToolbarIconButton from "./ToolbarIconButton";
import SwipeableThreadRow from "./SwipeableThreadRow";
import {
  getEmailContact,
  parseThreadKey,
} from "./threadGrouping";

function stringToColor(str) {
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "000000".substring(0, 6 - c.length) + c;
}

function sanitizeEmailFragment(html) {
  return String(html || "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<meta[^>]*http-equiv\s*=\s*["']?refresh["']?[^>]*>/gi, "")
    .replace(/on\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/on\w+\s*=\s*'[^']*'/gi, "")
    .replace(/--matrixboundary--/g, "")
    .replace(/<\/script/gi, "<\\/script");
}

function buildEmailSrcDoc(fragment, bodyBg = "#ffffff") {
  const inner = sanitizeEmailFragment(fragment);
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank" rel="noopener noreferrer"><style>
    html,body{height:auto!important;min-height:0!important;margin:0;padding:0;overflow:hidden;color:#1e293b;}
    body{padding:12px;background:${bodyBg};overflow-x:auto;}
    table{height:auto!important;}
    img{max-width:100%;height:auto!important;}
  </style></head><body>${inner}</body></html>`;
}

function measureIframeContentHeight(doc) {
  const body = doc.body;
  const html = doc.documentElement;
  if (!body) return 80;

  html.style.height = "auto";
  html.style.minHeight = "0";
  body.style.height = "auto";
  body.style.minHeight = "0";

  const scrollH = body.scrollHeight;
  const bodyRect = body.getBoundingClientRect();
  let maxBottom = 0;

  body.querySelectorAll("*").forEach((el) => {
    const style = doc.defaultView.getComputedStyle(el);
    if (style.display === "none" || style.visibility === "hidden") return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 1 && rect.height < 1) return;
    const relativeBottom = rect.bottom - bodyRect.top;
    if (relativeBottom > maxBottom) maxBottom = relativeBottom;
  });

  const paddingBottom =
    parseFloat(doc.defaultView.getComputedStyle(body).paddingBottom) || 0;
  const contentH = Math.ceil(maxBottom + paddingBottom);

  if (contentH > 80 && scrollH > contentH * 1.25) {
    return contentH;
  }
  return Math.max(scrollH, contentH, 80);
}

function EmailBodyIframe({ html }) {
  const c = useUniboxColors();
  const iframeRef = useRef(null);
  const srcDoc = useMemo(() => buildEmailSrcDoc(html, c.emailCanvasBg), [html, c.emailCanvasBg]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const resize = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) return;
        const h = measureIframeContentHeight(doc);
        iframe.style.height = `${h + 8}px`;
      } catch {
        iframe.style.height = "400px";
      }
    };

    const onLoad = () => {
      resize();
      requestAnimationFrame(resize);
      [50, 200, 600].forEach((ms) => setTimeout(resize, ms));
      try {
        iframe.contentDocument?.querySelectorAll?.("img").forEach((img) => {
          img.addEventListener("load", resize);
        });
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener("load", onLoad);
    return () => iframe.removeEventListener("load", onLoad);
  }, [srcDoc]);

  return (
    <iframe
      ref={iframeRef}
      title="Cuerpo del mensaje"
      srcDoc={srcDoc}
      sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
      style={{
        width: "100%",
        border: 0,
        display: "block",
        minHeight: 120,
        backgroundColor: c.emailCanvasBg,
      }}
    />
  );
}

function ThreadMessageRow({
  email,
  isActive,
  onOpen,
  selectionMode,
  selected,
  toggleSelectEmail,
  askDelete,
  openSwipeKey,
  setOpenSwipeKey,
}) {
  const c = useUniboxColors();
  const isSent = email.direction === "sent";

  const row = (
    <Box
      onClick={() => {
        if (selectionMode) {
          toggleSelectEmail(email.gmailId);
          return;
        }
        setOpenSwipeKey(null);
        onOpen(email);
      }}
      sx={{
        width: "100%",
        px: 1.25,
        py: 1.1,
        cursor: "pointer",
        borderRadius: c.radius,
        border: "1px solid",
        borderColor: isActive ? c.selectedBorder : selected ? c.accentBorder : c.border,
        bgcolor: isActive ? c.selected : selected ? c.accentSoft : c.card,
        boxShadow: isActive || selected ? c.shadowActive : c.shadowSm,
        transition: "all 0.15s ease",
        "&:hover": {
          borderColor: isActive || selected ? c.selectedBorder : c.border,
          bgcolor: isActive ? c.selected : selected ? c.accentSoft : c.cardHover,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
        {selectionMode && (
          <Checkbox
            size="small"
            checked={selected}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelectEmail(email.gmailId);
            }}
            sx={{ p: 0, mt: 0.25 }}
          />
        )}
        {!email.isRead && !selectionMode && (
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              bgcolor: c.unreadDot,
              flexShrink: 0,
              mt: 0.6,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 0.4,
            }}
          >
            <Chip
              label={isSent ? "Enviado" : "Recibido"}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: isSent ? c.sentBg : c.recvBg,
                color: isSent ? c.sentText : c.recvText,
              }}
            />
            <Typography sx={{ fontSize: 10, color: c.textDim, flexShrink: 0 }}>
              {new Date(email.date).toLocaleString("es-ES", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: email.isRead ? 600 : 800,
              color: c.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email.subject || "(Sin asunto)"}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: c.textMuted,
              mt: 0.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email.snippet}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  if (selectionMode) {
    return <Box sx={{ mx: 1, mb: 0.75 }}>{row}</Box>;
  }

  return (
    <Box sx={{ mx: 1, mb: 0.75 }}>
      <SwipeableThreadRow
        isOpen={openSwipeKey === email.gmailId}
        onOpen={() => setOpenSwipeKey(email.gmailId)}
        onClose={() => setOpenSwipeKey((prev) => (prev === email.gmailId ? null : prev))}
        onDelete={() => askDelete(email.gmailId)}
      >
        {row}
      </SwipeableThreadRow>
    </Box>
  );
}

function EmailReadingPane({
  email,
  body,
  loadingBody,
  attachments,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  restoreEmail,
  isTrashView,
  selected,
  toggleSelectEmail,
  isMobile,
}) {
  const c = useUniboxColors();

  if (!email) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: c.textMuted,
          p: 3,
        }}
      >
        <Typography sx={{ fontSize: 14 }}>Selecciona un mensaje de la lista</Typography>
      </Box>
    );
  }

  const isSent = email.direction === "sent";

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        overflowY: "auto",
        overflowX: "hidden",
        pt: 1,
        px: { xs: 1, md: 1.25 },
        pb: { xs: 1.25, md: 1.5 },
        bgcolor: c.panelAlt,
      }}
    >
      <Box
        sx={{
          bgcolor: c.card,
          borderRadius: c.radius,
          border: `1px solid ${c.border}`,
          boxShadow: c.shadowMd,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2.5 },
            py: 1.5,
            borderBottom: `1px solid ${c.metaBorder}`,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            bgcolor: c.metaBg,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Chip
              label={isSent ? "Enviado" : "Recibido"}
              size="small"
              sx={{
                mb: 0.75,
                fontSize: 11,
                fontWeight: 700,
                bgcolor: isSent ? c.sentBg : c.recvBg,
                color: isSent ? c.sentText : c.recvText,
              }}
            />
            <Typography sx={{ fontSize: 11, color: c.textDim }}>
              {new Date(email.date).toLocaleString("es-ES")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            <ToolbarIconButton
              title={selected ? "Quitar selección" : "Seleccionar"}
              onClick={() => toggleSelectEmail(email.gmailId)}
              active={selected}
              icon={
                selected ? (
                  <CheckBoxIcon sx={{ fontSize: "1rem" }} />
                ) : (
                  <CheckBoxOutlineBlankIcon sx={{ fontSize: "1rem" }} />
                )
              }
            />
            <ToolbarIconButton
              title={email.isRead ? "Marcar como no leído" : "Marcar como leído"}
              onClick={() => toggleRead(email.gmailId)}
              active={!email.isRead}
              icon={
                email.isRead ? (
                  <MailOutlineIcon sx={{ fontSize: "1rem" }} />
                ) : (
                  <DoneIcon sx={{ fontSize: "1rem" }} />
                )
              }
            />
            {!isTrashView ? (
              <>
                <ToolbarIconButton
                  title="Responder"
                  onClick={() => openReply(email)}
                  icon={<ReplyIcon sx={{ fontSize: "1rem" }} />}
                />
                <ToolbarIconButton
                  title="Reenviar"
                  onClick={() => openForward(email)}
                  icon={<ReplyIcon sx={{ fontSize: "1rem", transform: "scaleX(-1)" }} />}
                />
              </>
            ) : (
              <ToolbarIconButton
                title="Restaurar"
                onClick={() => restoreEmail(email.gmailId)}
                icon={<ReplyIcon sx={{ fontSize: "1rem", transform: "rotate(180deg)" }} />}
              />
            )}
            <ToolbarIconButton
              title="Eliminar"
              onClick={() => askDelete(email.gmailId)}
              variant="danger"
              icon={<DeleteIcon sx={{ fontSize: "1rem" }} />}
            />
          </Box>
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 2.5 }, py: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: 17, md: 20 },
              fontWeight: 800,
              color: c.textPrimary,
              lineHeight: 1.35,
              letterSpacing: "-0.02em",
              mb: 1.5,
            }}
          >
            {email.subject || "(Sin asunto)"}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 0.75,
              p: 1.5,
              borderRadius: 2,
              bgcolor: c.metaBg,
              border: `1px solid ${c.metaBorder}`,
              fontSize: 12,
            }}
          >
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "baseline" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.textDim, minWidth: 28 }}>
                De
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: c.textPrimary, wordBreak: "break-all" }}>
                {email.from}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "baseline" }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.textDim, minWidth: 28 }}>
                Para
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: c.textPrimary, wordBreak: "break-all" }}>
                {email.to_email}
              </Typography>
            </Box>
            {email.hasAttachments && (
              <Chip
                label="Adjuntos"
                size="small"
                sx={{
                  width: "fit-content",
                  mt: 0.25,
                  bgcolor: c.recvBg,
                  color: c.recvText,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 1.25, md: 2 },
            pb: 2,
          }}
        >
          {loadingBody ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={26} />
            </Box>
          ) : (
            <Box
              sx={{
                borderRadius: c.radius,
                overflow: "hidden",
                border: c.isDark ? `1px solid ${c.border}` : "none",
                bgcolor: c.emailCanvasBg,
              }}
            >
              <EmailBodyIframe html={body} />
            </Box>
          )}
          {attachments?.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              {attachments.map((a) => (
                <AttachmentPreview key={a.id} file={a} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function EmailPanelDerecho({
  threads,
  selectedPerson,
  filter = "all",
  folder = "inbox",
  bodyCache,
  attachmentsByEmail,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  loadBodyCached,
  loadAttachments,
  selectionMode,
  selectionBarMode,
  selectedEmails,
  toggleSelectEmail,
  enterSelectionMode,
  clearSelection,
  restoreEmail,
  isMobile = false,
  onBack,
  listGrouping = "account",
  focusGmailId = null,
  onFocusGmailIdClear,
}) {
  const c = useUniboxColors();
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [mobilePane, setMobilePane] = useState("reading");
  const [openSwipeKey, setOpenSwipeKey] = useState(null);

  const currentThread = useMemo(() => {
    const raw = threads[selectedPerson];
    const base = Array.isArray(raw) ? raw : [];
    let list = [...base];
    if (filter === "read") list = list.filter((e) => e.isRead);
    if (filter === "unread") list = list.filter((e) => !e.isRead);
    list.sort((a, b) => new Date(b.date) - new Date(a.date));
    return list;
  }, [threads, selectedPerson, filter]);

  const threadCountLabel =
    filter === "unread"
      ? `${currentThread.length} no leídos`
      : filter === "read"
        ? `${currentThread.length} leídos`
        : `${currentThread.length} mensajes`;

  const parsedSelection = useMemo(
    () => parseThreadKey(selectedPerson),
    [selectedPerson]
  );

  const currentContact = useMemo(() => {
    if (!selectedPerson) return "";
    if (parsedSelection.contact) return parsedSelection.contact;
    const first = currentThread[0];
    return first ? getEmailContact(first) : selectedPerson;
  }, [selectedPerson, parsedSelection.contact, currentThread]);

  const headerTitle = useMemo(() => {
    if (!selectedPerson) return "";
    if (listGrouping === "conversation") {
      return currentThread[0]?.subject?.trim() || "(Sin asunto)";
    }
    return currentContact;
  }, [selectedPerson, listGrouping, currentThread, currentContact]);

  const contactInitial = (currentContact.charAt(0) || "?").toUpperCase();

  const openMessage = (email) => {
    if (selectionMode) return;
    setSelectedEmail(email);
    loadBodyCached(email.gmailId);
    loadAttachments(email.gmailId);
    if (isMobile) setMobilePane("reading");
  };

  useEffect(() => {
    if (!currentThread.length) {
      setSelectedEmail(null);
      return;
    }
    setSelectedEmail((prev) => {
      if (!prev) return currentThread[0];
      const fresh = currentThread.find((e) => e.gmailId === prev.gmailId);
      return fresh ?? currentThread[0];
    });
  }, [currentThread]);

  useEffect(() => {
    if (isMobile) setMobilePane("reading");
    setOpenSwipeKey(null);
  }, [selectedPerson, isMobile]);

  useEffect(() => {
    if (!selectedPerson) return;
    const mails = threads[selectedPerson] || [];
    mails.forEach((m) => {
      loadBodyCached(m.gmailId);
      loadAttachments(m.gmailId);
    });
  }, [selectedPerson, threads, loadBodyCached, loadAttachments]);

  useEffect(() => {
    if (!focusGmailId || !currentThread.length) return;
    const email = currentThread.find((e) => e.gmailId === focusGmailId);
    if (email) {
      setSelectedEmail(email);
      loadBodyCached(email.gmailId);
      loadAttachments(email.gmailId);
      if (isMobile) setMobilePane("reading");
    }
    onFocusGmailIdClear?.();
  }, [
    focusGmailId,
    currentThread,
    loadBodyCached,
    loadAttachments,
    isMobile,
    onFocusGmailIdClear,
  ]);

  if (!selectedPerson) {
    return (
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: "hidden",
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          bgcolor: c.panelAlt,
          color: c.textMuted,
        }}
      >
        <Box sx={{ textAlign: "center", px: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: c.textPrimary }}>
            Selecciona una conversación
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 0.75, color: c.textMuted }}>
            Elige un contacto para ver el historial de mensajes.
          </Typography>
        </Box>
      </Box>
    );
  }

  const activeBody = selectedEmail ? bodyCache[selectedEmail.gmailId] || "" : "";
  const activeLoading = selectedEmail ? !bodyCache[selectedEmail.gmailId] : false;
  const activeAttachments = selectedEmail
    ? attachmentsByEmail[selectedEmail.gmailId] || []
    : [];

  const showMessageList = !isMobile || mobilePane === "messages";
  const showReadingPane = !isMobile || mobilePane === "reading";

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: c.panelAlt,
        overflow: "hidden",
        pt: selectionMode && selectionBarMode === "top" ? 6.5 : 0,
      }}
    >
      <Box
        sx={{
          px: { xs: 1.25, md: 2 },
          py: 1.25,
          borderBottom: `1px solid ${c.border}`,
          bgcolor: c.headerBg,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {isMobile && (
          <IconButton size="small" onClick={onBack} sx={{ mr: 0.25 }}>
            <ArrowBackIcon />
          </IconButton>
        )}

        <Avatar
          sx={{
            width: 36,
            height: 36,
            bgcolor: stringToColor(currentContact),
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          {contactInitial}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 14, md: 16 },
              fontWeight: 800,
              color: c.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {headerTitle}
          </Typography>
          <Typography sx={{ fontSize: 11, color: c.textDim }}>
            {listGrouping === "conversation" ? (
              <>
                {currentContact}
                {currentContact ? " · " : ""}
                {threadCountLabel}
              </>
            ) : (
              threadCountLabel
            )}
          </Typography>
        </Box>

        <Button
          size="small"
          onClick={() => {
            if (selectionMode) {
              clearSelection?.();
              return;
            }
            enterSelectionMode?.();
          }}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 700,
            color: selectionMode ? c.textPrimary : c.accent,
            flexShrink: 0,
            minWidth: 0,
            px: 0.75,
          }}
        >
          {selectionMode ? "Listo" : "Seleccionar"}
        </Button>

        {isMobile && (
          <IconButton
            size="small"
            onClick={() =>
              setMobilePane((p) => (p === "reading" ? "messages" : "reading"))
            }
            sx={{
              border: `1px solid ${c.border}`,
              bgcolor: mobilePane === "messages" ? c.accentSoft : c.toolbarBtn,
            }}
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          overflow: "hidden",
        }}
      >
        {showMessageList && (
          <Box
            sx={{
              width: isMobile ? "100%" : { md: "40%", lg: "38%" },
              minWidth: isMobile ? 0 : 260,
              maxWidth: isMobile ? "100%" : 420,
              flexShrink: 0,
              overflowY: "auto",
              bgcolor: c.panelAlt,
              pt: 1,
              pb: 1,
            }}
            onClick={() => setOpenSwipeKey(null)}
          >
            {currentThread.length === 0 ? (
              <Box sx={{ p: 2, color: c.textMuted }}>
                <Typography sx={{ fontSize: 13 }}>
                  No hay mensajes con este filtro.
                </Typography>
              </Box>
            ) : (
              currentThread.map((email) => (
                <ThreadMessageRow
                  key={email.gmailId}
                  email={email}
                  isActive={selectedEmail?.gmailId === email.gmailId}
                  onOpen={openMessage}
                  selectionMode={selectionMode}
                  selected={selectedEmails.includes(email.gmailId)}
                  toggleSelectEmail={toggleSelectEmail}
                  askDelete={askDelete}
                  openSwipeKey={openSwipeKey}
                  setOpenSwipeKey={setOpenSwipeKey}
                />
              ))
            )}
          </Box>
        )}

        {showReadingPane && (
          <EmailReadingPane
            email={selectedEmail}
            body={activeBody}
            loadingBody={activeLoading}
            attachments={activeAttachments}
            toggleRead={toggleRead}
            openReply={openReply}
            openForward={openForward}
            askDelete={askDelete}
            restoreEmail={restoreEmail}
            isTrashView={folder === "trash"}
            selected={
              selectedEmail ? selectedEmails.includes(selectedEmail.gmailId) : false
            }
            toggleSelectEmail={toggleSelectEmail}
            isMobile={isMobile}
          />
        )}
      </Box>
    </Box>
  );
}
